import { showToast } from "./toast";
import { memo, useRef, useState } from "react";
import type { ToolEvent, Turn, TurnUsage } from "../types";
import { brandingSlug, getBranding, useBranding } from "../branding";
import { exportMessageHtml } from "../utils/exportConversation";
import { toolToBusinessStep } from "../utils/toolToBusiness";
import { splitProcesso } from "../utils/processo";
import { inlineMermaidAsImages } from "../utils/mermaid";
import { MarkdownView } from "./Markdown";

interface Props {
  turn: Turn;
  turnIdx: number;
  isThinking?: boolean;
  currentStep?: ToolEvent | null;
  isLastAssistant?: boolean;
  onRetry?: () => void;
  onRegenerate?: () => void;
  onLike?: (idx: number) => void;
  onDislike?: (idx: number) => void;
  onSwitchVersion?: (idx: number, versionIdx: number) => void;
  onDelete?: (idx: number) => void;
}

/**
 * Memoizada: durante o streaming só o ÚLTIMO turno muda de identidade - os
 * anteriores não re-parseiam o markdown a cada token. Sem isso, resposta
 * longa travava a tela (caixa em branco) re-renderizando a conversa inteira.
 */
export const Message = memo(MessageInner);

function MessageInner({
  turn,
  turnIdx,
  isThinking,
  currentStep,
  isLastAssistant,
  onRetry,
  onRegenerate,
  onLike,
  onDislike,
  onSwitchVersion,
  onDelete,
}: Props) {
  const isUser = turn.role === "user";
  // separa narração de processo (<processo>…</processo>) da resposta final
  const parsed = isUser
    ? { answer: turn.content, processo: "", streaming: false }
    : splitProcesso(turn.content);
  const answer = parsed.answer;
  const showThinking = !isUser && isThinking && !answer && !turn.errored;
  const showError = !isUser && turn.errored;
  const stepText = currentStep ? toolToBusinessStep(currentStep.name, currentStep.input) : null;
  // enquanto ainda não há resposta, mostra a última linha da narração de processo
  const liveProcesso = parsed.processo ? lastLine(parsed.processo) : null;
  const thinkingText = liveProcesso ?? stepText ?? "Pensando…";
  const bubbleRef = useRef<HTMLDivElement>(null);
  const { appName } = useBranding();

  return (
    <div className={`msg msg-${turn.role}`}>
      {!isUser && (
        <div className="avatar avatar-assistant" aria-hidden>
          {appName.charAt(0).toUpperCase()}
        </div>
      )}
      <div className="msg-content">
        <div className="msg-author">{isUser ? "Você" : appName}</div>
        <div ref={bubbleRef} className={`msg-bubble ${showError ? "msg-bubble-error" : ""}`}>
          {isUser ? (
            <div className="user-content">{turn.content}</div>
          ) : showThinking ? (
            <div className="thinking" aria-label="Pensando">
              <span className="thinking-dot" />
              <span className="thinking-dot" />
              <span className="thinking-dot" />
              <span className="thinking-text">{thinkingText}</span>
            </div>
          ) : (
            <>
              {answer && (
                <div className="markdown">
                  <MarkdownView content={answer} />
                </div>
              )}
              {!turn.errored && turn.aborted && answer && (
                <div className="aborted-note" title="Resposta interrompida pela usuária">
                  <StopBadgeIcon />
                  Resposta interrompida
                </div>
              )}
              {!turn.errored && stepText && isThinking && (
                <div className="current-step">
                  <span className="thinking-dot" />
                  <span className="thinking-dot" />
                  <span className="thinking-dot" />
                  <span>{stepText}</span>
                </div>
              )}
              {!turn.errored && answer && (turn.usage || turn.durationMs != null) && (
                <TurnStats usage={turn.usage} costUsd={turn.costUsd} durationMs={turn.durationMs} agent={turn.agent} />
              )}
              {showError && (
                <div className="error-block">
                  <div className="error-message">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <circle cx="8" cy="8" r="6.5" />
                      <path d="M8 5v3.5M8 11h.01" />
                    </svg>
                    <span>
                      Não consegui responder.
                      {turn.errorMessage ? ` (${turn.errorMessage})` : ""}
                    </span>
                  </div>
                  {onRetry && (
                    <button className="retry-btn" onClick={onRetry} type="button">
                      <RefreshIcon />
                      Tentar novamente
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {/* Narração de bastidor, separada da resposta e recolhida por padrão */}
        {!isUser && !turn.errored && parsed.processo && (
          <ProcessoPanel text={parsed.processo} live={!!isThinking && parsed.streaming} />
        )}
        {/* Ações: usuário (só copiar), assistente (barra completa) */}
        {!showThinking && !turn.errored && (isUser ? turn.content : answer) && (
          isUser ? (
            <UserActions content={turn.content} turnIdx={turnIdx} onDelete={onDelete} />
          ) : (
            <AssistantActions
              turn={turn}
              answer={answer}
              turnIdx={turnIdx}
              isLastAssistant={!!isLastAssistant}
              bubbleRef={bubbleRef}
              onRegenerate={onRegenerate}
              onLike={onLike}
              onDislike={onDislike}
              onSwitchVersion={onSwitchVersion}
              onDelete={onDelete}
            />
          )
        )}
      </div>
      {isUser && (
        <div className="avatar avatar-user" aria-hidden>
          M
        </div>
      )}
    </div>
  );
}

/** Última linha não-vazia de um texto (pra mostrar a narração em andamento). */
function lastLine(text: string): string {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  return lines[lines.length - 1] ?? "";
}

/**
 * Painel recolhível "Como cheguei nisso" — a narração de bastidor do assistente,
 * separada da resposta final. Fica fora do balão, então não entra nas
 * exportações da resposta. Tem cópia própria.
 */
function ProcessoPanel({ text, live }: { text: string; live: boolean }) {
  // recolhido por padrão: o feedback ao vivo já aparece na bolha "pensando"
  const [open, setOpen] = useState(false);
  return (
    <details className="processo-panel" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>
        <svg className="caret" width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M6 4l4 4-4 4" />
        </svg>
        {live ? "Trabalhando…" : "Como cheguei nisso"}
        <span className="processo-copy" onClick={(e) => e.preventDefault()}>
          <CopyButton content={text} label="Copiar o processo" />
        </span>
      </summary>
      <div className="processo-body markdown">
        <MarkdownView content={text} />
      </div>
    </details>
  );
}

function TurnStats({
  usage,
  costUsd,
  durationMs,
  agent,
}: {
  usage?: TurnUsage;
  costUsd?: number;
  durationMs?: number;
  agent?: "claude" | "codex" | "gemini";
}) {
  const totalTokens = usage ? (usage.inputTokens ?? 0) + (usage.outputTokens ?? 0) : 0;
  const parts: string[] = [];
  // qual IA gerou esta resposta - transparência do gasto por motor
  if (agent) parts.push(agent === "codex" ? "Codex" : agent === "gemini" ? "Gemini" : "Claude");
  if (totalTokens > 0) parts.push(`${formatTokens(totalTokens)} tokens`);
  if (usage?.outputTokens) parts.push(`${formatTokens(usage.outputTokens)} de saída`);
  if (typeof costUsd === "number" && costUsd > 0) parts.push(`$${costUsd.toFixed(4)}`);
  if (typeof durationMs === "number" && durationMs > 0) parts.push(`${(durationMs / 1000).toFixed(1)}s`);
  if (parts.length === 0) return null;
  return <div className="turn-stats">{parts.join(" • ")}</div>;
}

function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

function UserActions({
  content,
  turnIdx,
  onDelete,
}: {
  content: string;
  turnIdx: number;
  onDelete?: (idx: number) => void;
}) {
  return (
    <div className="msg-actions msg-actions-user">
      <CopyButton content={content} label="Copiar pergunta" />
      {onDelete && <DeleteButton turnIdx={turnIdx} onDelete={onDelete} />}
    </div>
  );
}

/** Apaga uma mensagem (soft delete). Confirma num clique duplo pra não apagar sem querer. */
function DeleteButton({ turnIdx, onDelete }: { turnIdx: number; onDelete: (idx: number) => void }) {
  const [armed, setArmed] = useState(false);
  const label = armed ? "Clique de novo para apagar" : "Apagar mensagem";
  return (
    <button
      className="action-btn"
      type="button"
      title={label}
      aria-label={label}
      onClick={() => {
        if (armed) onDelete(turnIdx);
        else setArmed(true);
      }}
      onBlur={() => setArmed(false)}
      style={armed ? { color: "var(--danger, #d64545)" } : undefined}
    >
      {armed ? "Confirmar?" : "🗑"}
    </button>
  );
}

function AssistantActions({
  turn,
  answer,
  turnIdx,
  isLastAssistant,
  bubbleRef,
  onRegenerate,
  onLike,
  onDislike,
  onSwitchVersion,
  onDelete,
}: {
  turn: Turn;
  answer: string;
  turnIdx: number;
  isLastAssistant: boolean;
  bubbleRef: React.RefObject<HTMLDivElement | null>;
  onRegenerate?: () => void;
  onLike?: (idx: number) => void;
  onDislike?: (idx: number) => void;
  onSwitchVersion?: (idx: number, versionIdx: number) => void;
  onDelete?: (idx: number) => void;
}) {
  const versions = turn.versions ?? [];
  const versionIdx = turn.versionIdx ?? Math.max(0, versions.length - 1);
  const hasVersions = versions.length > 1;

  return (
    <div className="msg-actions msg-actions-assistant">
      {hasVersions && onSwitchVersion && (
        <div className="version-pager" role="group" aria-label="Navegar entre versões">
          <button
            className="action-btn icon-only"
            onClick={() => onSwitchVersion(turnIdx, Math.max(0, versionIdx - 1))}
            disabled={versionIdx === 0}
            type="button"
            aria-label="Versão anterior"
            title="Versão anterior"
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M10 4l-4 4 4 4" />
            </svg>
          </button>
          <span className="version-counter">{versionIdx + 1} / {versions.length}</span>
          <button
            className="action-btn icon-only"
            onClick={() => onSwitchVersion(turnIdx, Math.min(versions.length - 1, versionIdx + 1))}
            disabled={versionIdx >= versions.length - 1}
            type="button"
            aria-label="Próxima versão"
            title="Próxima versão"
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M6 4l4 4-4 4" />
            </svg>
          </button>
        </div>
      )}
      <CopyButton content={answer} label="Copiar resposta" />
      <FeedbackButton
        kind="like"
        active={turn.feedback === "like"}
        onClick={() => onLike?.(turnIdx)}
      />
      <FeedbackButton
        kind="dislike"
        active={turn.feedback === "dislike"}
        onClick={() => onDislike?.(turnIdx)}
      />
      {isLastAssistant && onRegenerate && (
        <button className="action-btn" onClick={onRegenerate} type="button" title="Gerar uma nova versão da resposta">
          <RefreshIcon />
          Tentar novamente
        </button>
      )}
      <button
        className="action-btn"
        type="button"
        title="Baixar resposta como Markdown"
        onClick={() => exportMarkdown(answer)}
      >
        <DownloadIcon />
        .md
      </button>
      <button
        className="action-btn"
        type="button"
        title="Baixar resposta como HTML autocontido"
        onClick={() => {
          void exportMessageHtml({ ...turn, content: answer });
        }}
      >
        <DownloadIcon />
        .html
      </button>
      <button
        className="action-btn"
        type="button"
        title="Baixar resposta como PDF"
        onClick={() => {
          void exportPdf(bubbleRef.current?.innerHTML ?? "");
        }}
      >
        <DownloadIcon />
        PDF
      </button>
      {onDelete && <DeleteButton turnIdx={turnIdx} onDelete={onDelete} />}
    </div>
  );
}

function CopyButton({ content, label }: { content: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = content;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* noop */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button className="action-btn" onClick={copy} type="button" title={label} aria-label={label}>
      {copied ? (
        <>
          <CheckIcon />
          Copiado
        </>
      ) : (
        <>
          <CopyIcon />
          Copiar
        </>
      )}
    </button>
  );
}

function FeedbackButton({
  kind,
  active,
  onClick,
}: {
  kind: "like" | "dislike";
  active: boolean;
  onClick: () => void;
}) {
  const label = kind === "like" ? "Gostei desta resposta" : "Não gostei desta resposta";
  return (
    <button
      className={`action-btn icon-only ${active ? "active" : ""}`}
      onClick={onClick}
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
    >
      {kind === "like" ? <ThumbUpIcon /> : <ThumbDownIcon />}
    </button>
  );
}

function exportMarkdown(content: string): void {
  const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${brandingSlug()}-${new Date().toISOString().replace(/[:.]/g, "-")}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/**
 * Gera o PDF direto no client e dispara o download — sem dialog de impressão.
 * Usa html2pdf.js (lazy-import pra não inflar o bundle inicial).
 */
async function exportPdf(innerHtml: string): Promise<void> {
  if (!innerHtml || !innerHtml.trim()) {
    showToast("Nada para exportar nesta resposta.", "error");
    return;
  }

  // html2canvas honra `opacity: 0`, `visibility: hidden`, e ignora elementos
  // fora do viewport. Solução: parent escondido por `overflow: hidden` em 0x0,
  // mas o container **interno** mantém dimensões e opacidade reais.
  const clip = document.createElement("div");
  clip.setAttribute("aria-hidden", "true");
  clip.style.position = "fixed";
  clip.style.top = "0";
  clip.style.left = "0";
  clip.style.width = "0";
  clip.style.height = "0";
  clip.style.overflow = "hidden";
  clip.style.pointerEvents = "none";
  clip.style.zIndex = "-1";

  const container = document.createElement("div");
  container.style.width = "180mm"; // largura útil A4 (210mm - 30mm margem)
  container.style.padding = "0";
  container.style.background = "#ffffff";
  container.style.color = "#1f1e1d";
  container.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
  container.style.fontSize = "12pt";
  container.style.lineHeight = "1.55";
  container.innerHTML = `
    <style>
      .pdf-doc h1, .pdf-doc h2, .pdf-doc h3, .pdf-doc h4 { font-weight: 600; margin: 12pt 0 6pt; }
      .pdf-doc h1 { font-size: 18pt; }
      .pdf-doc h2 { font-size: 15pt; }
      .pdf-doc h3 { font-size: 13pt; }
      .pdf-doc p { margin: 0 0 8pt; }
      .pdf-doc ul, .pdf-doc ol { margin: 0 0 8pt; padding-left: 18pt; }
      .pdf-doc li { margin: 2pt 0; }
      .pdf-doc pre { background: #f4f1ea; padding: 8pt 10pt; border-radius: 4pt; font-family: "JetBrains Mono", ui-monospace, monospace; font-size: 10pt; white-space: pre-wrap; word-wrap: break-word; overflow: hidden; }
      .pdf-doc code { background: #f4f1ea; padding: 1pt 4pt; border-radius: 2pt; font-family: "JetBrains Mono", ui-monospace, monospace; font-size: 10pt; color: #c15f3c; }
      .pdf-doc pre code { background: transparent; padding: 0; color: inherit; }
      .pdf-doc blockquote { border-left: 3pt solid #f4dccd; padding-left: 8pt; color: #3d3b38; margin: 6pt 0; }
      .pdf-doc table { border-collapse: collapse; width: 100%; margin: 8pt 0; font-size: 10.5pt; }
      .pdf-doc th, .pdf-doc td { border: 1pt solid #e8e3da; padding: 5pt 7pt; text-align: left; }
      .pdf-doc th { background: #f0ede6; }
      .pdf-doc a { color: #c15f3c; text-decoration: none; }
      .pdf-doc hr { border: none; border-top: 1pt solid #e8e3da; margin: 10pt 0; }
      .pdf-doc .turn-stats, .pdf-doc .current-step, .pdf-doc .error-block { display: none; }
      .pdf-meta { font-size: 9pt; color: #6b6964; margin-bottom: 12pt; padding-bottom: 8pt; border-bottom: 1pt solid #e8e3da; }
      .pdf-footer { font-size: 8pt; color: #97948d; margin-top: 14pt; padding-top: 8pt; border-top: 1pt solid #e8e3da; }
    </style>
    <div class="pdf-meta">${getBranding().appName} — exportado em ${new Date().toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}</div>
    <div class="pdf-doc">${innerHtml}</div>
    <div class="pdf-footer">Gerado pelo ${getBranding().appName}</div>
  `;
  clip.appendChild(container);
  document.body.appendChild(clip);

  try {
    // Força reflow + 2 frames pra garantir que o layout terminou.
    container.getBoundingClientRect();
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );

    // Sanity check: o container tem dimensões reais?
    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      throw new Error(`Container sem dimensões (${rect.width}x${rect.height}).`);
    }

    // Converte diagramas Mermaid (SVG inline do balão) em PNG — o html2canvas
    // não rasteriza SVG de forma confiável, então o desenho sairia ilegível.
    await inlineMermaidAsImages(container, "png");

    // Lazy import: html2pdf.js só vem se o usuário clicar exportar
    const mod = await import("html2pdf.js");
    // html2pdf.js exporta UMD; em ESM pode vir como default ou named
    const html2pdf =
      (mod as { default?: () => unknown }).default ??
      ((mod as unknown) as () => unknown);

    if (typeof html2pdf !== "function") {
      throw new Error("html2pdf não carregou corretamente.");
    }

    const filename = `${brandingSlug()}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.pdf`;
    await (html2pdf as () => {
      set: (o: unknown) => { from: (e: HTMLElement) => { save: () => Promise<void> } };
    })()
      .set({
        margin: [15, 15, 15, 15],
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      })
      .from(container)
      .save();
  } catch (err) {
    console.error("Erro ao gerar PDF:", err);
    showToast(`Não consegui gerar o PDF: ${err instanceof Error ? err.message : String(err)}`, "error");
  } finally {
    if (clip.parentNode) clip.parentNode.removeChild(clip);
  }
}

/* ---------- Icons ---------- */

function StopBadgeIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <rect x="3.5" y="3.5" width="9" height="9" rx="1.5" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="4" y="4" width="9" height="10" rx="1.5" />
      <path d="M3 11V3a1 1 0 0 1 1-1h8" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 8.5l3 3 7-7" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M13.5 8a5.5 5.5 0 1 1-1.61-3.89" />
      <path d="M13.5 2.5v3h-3" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8 2v9M4.5 7.5L8 11l3.5-3.5" />
      <path d="M3 13h10" />
    </svg>
  );
}

function ThumbUpIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 7v6h2V7zm3.5 0V4.5a1.5 1.5 0 0 1 3 0V7H12a1.5 1.5 0 0 1 1.48 1.78l-.7 3.5A1.5 1.5 0 0 1 11.3 13.5H5.5V7z" />
    </svg>
  );
}

function ThumbDownIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 9V3h2v6zm3.5 0V11.5a1.5 1.5 0 0 0 3 0V9H12a1.5 1.5 0 0 0 1.48-1.78l-.7-3.5A1.5 1.5 0 0 0 11.3 2.5H5.5V9z" />
    </svg>
  );
}
